import { useState, useEffect, useCallback, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../utils/firebase';
import * as Sentry from '@sentry/browser';

function messageForPreflightFailure(err) {
  const code = typeof err?.code === 'string' ? err.code : '';
  const msg = typeof err?.message === 'string' ? err.message : '';
  const isNotFound =
    code === 'functions/not-found' ||
    /\bnot-found\b/i.test(code) ||
    /\b404\b/.test(msg);
  if (isNotFound) {
    return {
      summary:
        'Delete check failed: the server could not find this brewery (404). It may have been removed, or the delete function URL or deployment may be wrong.',
      detail: msg || code || null,
    };
  }
  return {
    summary:
      'Delete check failed: could not verify whether this brewery can be deleted. Try again later or contact support if this continues.',
    detail: msg || code || null,
  };
}

/**
 * Custom hook for brewery delete operations
 * Provides preflight check, delete execution, and state management
 * @param {string} breweryId - The ID of the brewery to delete
 * @returns {Object} Object containing canDelete, deleteLoading, deleteBrewery, and errorMessage
 */
export function useBreweryDelete(breweryId) {
  const [canDelete, setCanDelete] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [preflightError, setPreflightError] = useState(null);
  const [preflightLoading, setPreflightLoading] = useState(false);
  const [counts, setCounts] = useState({
    companyLocations: 0,
    hospitalityReviews: 0,
    beerReviews: 0,
    checkIns: 0,
    userFavsDocs: 0,
    beers: 0,
    totalEstimatedWritesForDelete: 0,
  });
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Preflight check for delete eligibility
  useEffect(() => {
    let isMounted = true;
    async function runPreflight() {
      try {
        setPreflightLoading(true);
        setPreflightError(null);
        const deleteBrewery = httpsCallable(
          functions,
          'callableBreweryDeleteBrewery-deleteBrewery',
        );
        const preflight = await deleteBrewery({ breweryId });
        if (!isMounted) return;
        setCanDelete(Boolean(preflight?.data?.canDelete));
        const nextCounts = preflight?.data?.counts || {};
        setCounts((prev) => ({
          ...prev,
          ...Object.fromEntries(
            Object.entries(prev).map(([k, v]) => [
              k,
              Number(nextCounts?.[k] ?? v),
            ]),
          ),
        }));
      } catch (err) {
        Sentry.captureException(err);
        if (!isMounted) return;
        setCanDelete(false);
        setPreflightError(messageForPreflightFailure(err));
        // keep counts as-is on error
      } finally {
        if (isMounted) setPreflightLoading(false);
      }
    }
    if (breweryId) {
      runPreflight();
    }
    return () => {
      isMounted = false;
    };
  }, [breweryId]);

  /**
   * Execute the delete operation
   * @returns {Promise<Object>} Result object with deleted status and error message if any
   */
  const deleteBrewery = useCallback(async () => {
    if (!isMountedRef.current) return { deleted: false };
    setDeleteLoading(true);
    setErrorMessage('');
    try {
      const deleteBreweryFn = httpsCallable(
        functions,
        'callableBreweryDeleteBrewery-deleteBrewery',
      );
      const result = await deleteBreweryFn({
        breweryId,
        performDelete: true,
      });
      if (result?.data?.deleted) {
        return { deleted: true };
      }
      // If not deleted for any reason, re-run preflight to refresh state
      if (!isMountedRef.current) return { deleted: false };
      const errorMsg =
        'Unable to delete brewery. Please check if it has associated beers or other dependencies.';
      setErrorMessage(errorMsg);
      const preflight = await deleteBreweryFn({ breweryId });
      if (!isMountedRef.current) return { deleted: false };
      setCanDelete(Boolean(preflight?.data?.canDelete));
      return { deleted: false, error: errorMsg };
    } catch (err) {
      Sentry.captureException(err);
      if (isMountedRef.current) {
        const errorMsg =
          'An error occurred while deleting the brewery. Please try again.';
        setErrorMessage(errorMsg);
        return { deleted: false, error: errorMsg };
      }
      return { deleted: false };
    } finally {
      // Only reset loading if we didn't navigate away
      if (isMountedRef.current) {
        setDeleteLoading(false);
      }
    }
  }, [breweryId]);

  /**
   * Manually check delete eligibility (re-run preflight)
   * @returns {Promise<boolean>} Whether the brewery can be deleted
   */
  const checkDeleteEligibility = useCallback(async () => {
    try {
      setPreflightLoading(true);
      setPreflightError(null);
      const deleteBreweryFn = httpsCallable(
        functions,
        'callableBreweryDeleteBrewery-deleteBrewery',
      );
      const preflight = await deleteBreweryFn({ breweryId });
      if (isMountedRef.current) {
        const canDeleteValue = Boolean(preflight?.data?.canDelete);
        setCanDelete(canDeleteValue);
        const nextCounts = preflight?.data?.counts || {};
        setCounts((prev) => ({
          ...prev,
          ...Object.fromEntries(
            Object.entries(prev).map(([k, v]) => [
              k,
              Number(nextCounts?.[k] ?? v),
            ]),
          ),
        }));
        return canDeleteValue;
      }
      return false;
    } catch (err) {
      Sentry.captureException(err);
      if (isMountedRef.current) {
        setCanDelete(false);
        setPreflightError(messageForPreflightFailure(err));
      }
      return false;
    } finally {
      if (isMountedRef.current) setPreflightLoading(false);
    }
  }, [breweryId]);

  return {
    canDelete,
    deleteLoading,
    deleteBrewery,
    checkDeleteEligibility,
    errorMessage,
    preflightError,
    preflightLoading,
    counts,
  };
}
